// auth.interceptor.ts
import { Injectable } from "@angular/core";
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { Router } from "@angular/router";
import { SafeStorageService } from '../shared/safe-storage.service';
import { AuthService } from "../services/auth.service";
import { LanguageService } from "./language.service";

/** Chave onde a mensagem do backend fica guardada ate a tela de login exibi-la. */
export const CHAVE_MOTIVO_LOGOUT = 'motivoLogout';

/** Codigos em que o backend derruba a sessao e manda uma explicacao para o usuario. */
const MOTIVOS_DE_SESSAO = ['TOKEN_EXPIRADO', 'SESSAO_ENCERRADA'];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private safe: SafeStorageService,
    private auth: AuthService,
    private languageService: LanguageService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.safe.getItem("authToken");
    const lang = this.languageService.getCurrent();

    const headers: { [name: string]: string } = {
      'Accept-Language': lang,
    };

    // Nao enviar token em requisicoes de login (o endpoint de login nao precisa de token
    // e enviar um token expirado pode causar rejeicao pelo AuthorizationFilter do backend)
    const ehRequisicaoDeLogin = req.url.endsWith('/login');
    if (token && !ehRequisicaoDeLogin) {
      headers['Authorization'] = `${token}`;
    }

    req = req.clone({ setHeaders: headers });

    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          // So fazer logout automatico se a requisicao foi enviada COM um token.
          // Se token era nulo, e uma requisicao anonima rejeitada (ex: durante bootstrap
          // do APP_INITIALIZER antes da sessao ser lida) — nao deve causar logout.
          const requisicaoTinhaToken = !!token;

          const urlAtual = this.router.url;
          const estaNoLogin = urlAtual === '/login' || urlAtual.startsWith('/login?');

          if (requisicaoTinhaToken && !estaNoLogin) {
            // Limpa sessao + atualiza estado global + redireciona
            this.auth.clearSessionAndBroadcast();
            // O backend derrubou este token: `TOKEN_EXPIRADO` (as 24h acabaram — nao ha
            // renovacao) ou `SESSAO_ENCERRADA` (logout em outro lugar, ou a vaga desta
            // plataforma foi para outro dispositivo). Guarda a mensagem dele, ja traduzida,
            // para o login explicar o motivo em vez de deixar o usuario achar que caiu sozinho.
            const codigo = error?.error?.codigo;
            const mensagem = error?.error?.message;
            if (MOTIVOS_DE_SESSAO.includes(codigo) && mensagem) {
              this.safe.setItem(CHAVE_MOTIVO_LOGOUT, mensagem);
            }
            this.router.navigate(["/login"]);
          }
        }
        return throwError(() => error);
      })
    );
  }
}
